import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminApiClient } from './adminApiClient'

const PERIODS = [1, 7, 30, 90]
const OVERVIEW_CONTRACT_VERSION = 2
const TTL = { overview: 2 * 60 * 1000, realtime: 45 * 1000 }
const RETENTION = { overview: 60 * 60 * 1000, realtime: 5 * 60 * 1000 }
const cache = new Map()
const inFlight = new Map()
const realtimeNetworkInFlight = new Map()
const dayInFlight = new Map()
const listeners = new Set()

const safePeriod = period => PERIODS.includes(period) ? period : 30
const safeDate = date => /^\d{8}$/.test(String(date || '')) ? String(date) : null
const safeCompare = compare => ['yesterday', 'lastWeek', 'both', 'previousPeriod'].includes(compare) ? compare : 'none'
const keyFor = (type, period = 30, date = null, compare = 'none', details = false) => type === 'overview' ? `analytics:v${OVERVIEW_CONTRACT_VERSION}:overview:${safePeriod(period)}:${safeDate(date) || 'today'}:${safeCompare(compare)}:${details ? 'details' : 'summary'}` : 'analytics:realtime'
const notify = () => listeners.forEach(listener => listener())
const subscribe = listener => { listeners.add(listener); return () => listeners.delete(listener) }
const entryFor = key => cache.get(key)
const isFresh = (entry, type) => entry && Date.now() - entry.updatedAt < TTL[type]
const isRetained = (entry, type) => entry && Date.now() - entry.updatedAt < RETENTION[type]

function fetchRealtimeNetwork(params) {
  if (realtimeNetworkInFlight.has('realtime')) return realtimeNetworkInFlight.get('realtime')
  const promise = adminApiClient.get('/api/admin/analytics/realtime', { params })
    .then(({ data: body }) => ({ body }))
    .finally(() => realtimeNetworkInFlight.delete('realtime'))
  realtimeNetworkInFlight.set('realtime', promise)
  return promise
}

async function fetchAdminAnalyticsNetwork({ refresh = false, period = 30, date = null, compare = 'none', details = false } = {}) {
  const safe = safePeriod(period)
  const params = { period: safe }
  if (safeDate(date)) params.date = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
  if (safeCompare(compare) !== 'none') params.compare = safeCompare(compare)
  if (details) params.details = 1
  if (refresh) params.refresh = 1
  const { data: overview } = await adminApiClient.get('/api/admin/analytics/overview', { params })
  if (overview.contractVersion !== OVERVIEW_CONTRACT_VERSION) {
    const error = new Error('analytics_contract_incompatible')
    error.code = 'analytics_contract_incompatible'
    throw error
  }
  return overview
}

function isValidOverview(data) {
  return data?.contractVersion === OVERVIEW_CONTRACT_VERSION
    && data?.status !== 'partial' && data?.status !== 'error'
    && data?.anchorMetrics && Object.hasOwn(data.anchorMetrics, 'dau')
    && Object.hasOwn(data.anchorMetrics, 'wau') && Object.hasOwn(data.anchorMetrics, 'mau')
    && data?.periodSummary && Object.hasOwn(data.periodSummary, 'sessions')
}

function isValidRealtime(data) {
  return data?.status === 'ok' && data?.realtime && Number.isFinite(Number(data.realtime.activeUsers))
}

function startRealtimeFetch({ refresh = false, period = 30, date = null, compare = 'none', details = false } = {}) {
  const key = 'analytics:realtime:request'
  if (realtimeNetworkInFlight.has(key)) return realtimeNetworkInFlight.get(key)
  const params = { period: safePeriod(period) }
  if (safeDate(date)) params.date = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
  if (safeCompare(compare) !== 'none') params.compare = safeCompare(compare)
  if (details) params.details = 1
  if (refresh) params.refresh = 1
  const promise = fetchRealtimeNetwork(params).then(({ body }) => {
    if (!isValidRealtime(body)) {
      const error = new Error(body.code || 'analytics_unavailable')
      error.code = body.code || 'analytics_unavailable'
      throw error
    }
    cache.set(keyFor('realtime'), { data: body.realtime, updatedAt: Date.now(), fetchedAt: body.realtime.fetchedAt || body.fetchedAt || null, error: null, status: 'ready' })
    notify()
    return body.realtime
  }).catch(error => {
    const entry = entryFor(keyFor('realtime'))
    if (entry) { entry.error = error; entry.lastAttemptAt = Date.now(); notify() }
    throw error
  }).finally(() => realtimeNetworkInFlight.delete(key))
  realtimeNetworkInFlight.set(key, promise)
  notify()
  return promise
}

function updateCache(period, date, compare, details, data) {
  const updatedAt = Date.now()
  if (!isValidOverview(data)) return false
  cache.set(keyFor('overview', period, date, compare, details), { data, updatedAt, fetchedAt: data.fetchedAt || null, error: null, status: 'ready' })
  notify()
  return true
}

function startFetch({ period = 30, date = null, compare = 'none', details = false, refresh = false } = {}) {
  const safe = safePeriod(period)
  const key = `analytics:v${OVERVIEW_CONTRACT_VERSION}:bundle:${safe}:${safeDate(date) || 'today'}:${safeCompare(compare)}:${details ? 'details' : 'summary'}`
  if (inFlight.has(key)) return inFlight.get(key)
  const promise = fetchAdminAnalyticsNetwork({ refresh, period: safe, date, compare, details })
    .then(data => {
      updateCache(safe, date, compare, details, data)
      const realtimeRequest = startRealtimeFetch({ refresh, period: safe, date, compare, details })
      if (refresh) {
        return realtimeRequest.then(realtime => ({ ...data, realtime }))
      }
      realtimeRequest.catch(() => null)
      const realtime = entryFor(keyFor('realtime'))?.data
      return realtime ? { ...data, realtime } : data
    })
    .catch(error => {
      const overview = entryFor(keyFor('overview', safe, date, compare, details))
      if (overview) { overview.error = error; notify() }
      throw error
    })
    .finally(() => {
      inFlight.delete(key)
      notify()
    })
  inFlight.set(key, promise)
  notify()
  return promise
}

export function getAnalyticsCacheState(period = 30, date = null, compare = 'none', details = false) {
  const overview = entryFor(keyFor('overview', period, date, compare, details))
  const realtime = entryFor(keyFor('realtime'))
  const data = overview?.data ? { ...overview.data, ...(overview?.fetchedAt ? { fetchedAt: overview.fetchedAt } : {}), ...(realtime?.data ? { realtime: realtime.data, realtimeFetchedAt: realtime.fetchedAt || realtime.data.fetchedAt || null } : {}) } : null
  return { data, updatedAt: overview?.updatedAt || null, error: overview?.error || null, refreshing: Boolean(inFlight.get(`analytics:v${OVERVIEW_CONTRACT_VERSION}:bundle:${safePeriod(period)}:${safeDate(date) || 'today'}:${safeCompare(compare)}:${details ? 'details' : 'summary'}`)) }
}

export function prefetchAdminAnalytics(options = {}) {
  const period = safePeriod(options.period)
  const current = getAnalyticsCacheState(period, options.date, options.compare, options.details)
  if (current.data && isFresh(entryFor(keyFor('overview', period, options.date, options.compare, options.details)), 'overview') && isFresh(entryFor(keyFor('realtime')), 'realtime')) return Promise.resolve(current.data)
  return startFetch({ period, date: options.date, compare: options.compare, details: options.details }).catch(() => null)
}

export function refreshAdminAnalytics(period = 30, options = {}) {
  return startFetch({ period, date: options.date, compare: options.compare, details: options.details, refresh: true })
}

export function clearAdminAnalyticsCache() {
  cache.clear()
  inFlight.clear()
  dayInFlight.clear()
  realtimeNetworkInFlight.clear()
  notify()
}

export function fetchAdminAnalytics(options = {}) {
  const period = safePeriod(options.period)
  const current = getAnalyticsCacheState(period, options.date, options.compare, options.details)
  const overview = entryFor(keyFor('overview', period, options.date, options.compare, options.details))
  const realtime = entryFor(keyFor('realtime'))
  if (!options.refresh && current.data && isRetained(overview, 'overview')) {
    if (!isFresh(overview, 'overview')) startFetch({ period, date: options.date, compare: options.compare, details: options.details }).catch(() => null)
    if (!isFresh(realtime, 'realtime')) startRealtimeFetch({ period, date: options.date, compare: options.compare, details: options.details }).catch(() => null)
    return Promise.resolve(current.data)
  }
  return startFetch({ period, date: options.date, compare: options.compare, details: options.details, refresh: Boolean(options.refresh) })
}

export function useAdminAnalytics({ period = 30, date = null, compare = 'none', details = false } = {}) {
  const safe = safePeriod(period)
  const [state, setState] = useState(() => getAnalyticsCacheState(safe, date, compare, details))
  const sync = useCallback(() => setState(getAnalyticsCacheState(safe, date, compare, details)), [safe, date, compare, details])
  useEffect(() => subscribe(sync), [sync])
  useEffect(() => {
    setState(current => ({ ...getAnalyticsCacheState(safe, date, compare, details), refreshing: !current.data || getAnalyticsCacheState(safe, date, compare, details).refreshing }))
    fetchAdminAnalytics({ period: safe, date, compare, details }).catch(() => setState(getAnalyticsCacheState(safe, date, compare, details)))
  }, [safe, date, compare, details])
  const refresh = useCallback(() => {
    setState(current => ({ ...current, refreshing: true, error: null }))
    return refreshAdminAnalytics(safe, { date, compare, details }).catch(error => { setState(getAnalyticsCacheState(safe, date, compare, details)); throw error })
  }, [safe, date, compare, details])
  return useMemo(() => ({ ...state, loading: !state.data && state.refreshing, refresh }), [state, refresh])
}

const dayKey = date => `analytics:v3:day:${safeDate(date) || 'today'}`

async function fetchAnalyticsDayNetwork(date, refresh = false) {
  const params = {}
  if (safeDate(date)) params.date = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
  if (refresh) params.refresh = 1
  const { data } = await adminApiClient.get('/api/admin/analytics/day', { params })
  return data
}

function fetchAnalyticsDay(date, refresh = false) {
  const key = dayKey(date)
  const current = cache.get(key)
  if (!refresh && current && isRetained(current, 'overview')) return Promise.resolve(current.data)
  if (dayInFlight.has(key)) return dayInFlight.get(key)
  const promise = fetchAnalyticsDayNetwork(date, refresh)
    .then(data => { cache.set(key, { data, updatedAt: Date.now(), error: null }); notify(); return data })
    .catch(error => { if (current) { current.error = error; notify() } throw error })
    .finally(() => dayInFlight.delete(key))
  dayInFlight.set(key, promise)
  notify()
  return promise
}

export function useAnalyticsDay(date) {
  const key = dayKey(date)
  const [state, setState] = useState(() => {
    const entry = cache.get(key)
    return { data: entry?.data || null, error: entry?.error || null, refreshing: Boolean(dayInFlight.get(key)) }
  })
  const sync = useCallback(() => {
    const entry = cache.get(key)
    setState({ data: entry?.data || null, error: entry?.error || null, refreshing: Boolean(dayInFlight.get(key)) })
  }, [key])
  useEffect(() => subscribe(sync), [sync])
  useEffect(() => {
    if (!date) return
    const entry = cache.get(key)
    setState({ data: entry?.data || null, error: entry?.error || null, refreshing: !entry?.data || Boolean(dayInFlight.get(key)) })
    fetchAnalyticsDay(date).catch(() => sync())
  }, [date, key, sync])
  const refresh = useCallback(() => fetchAnalyticsDay(date, true), [date])
  return useMemo(() => ({ ...state, loading: !state.data && state.refreshing, refresh }), [state, refresh])
}
